import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { createFighterNote, getFighterNotes } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import { 
  MessageSquare, 
  User, 
  Send, 
  ArrowLeft,
  Filter,
  ClipboardList,
  Target,
  Swords
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format, parseISO, isValid } from 'date-fns';
import { cn } from '../lib/utils';

const safeDateFormat = (dateStr, formatStr) => {
  if (!dateStr) return 'N/A';
  const date = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
  return isValid(date) ? format(date, formatStr) : 'N/A';
};

const FighterNotesPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filterType, setFilterType] = useState('all');
  
  // New note form state
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');

  const loadNotes = useCallback(async () => {
    try {
      const params = {};
      if (filterType && filterType !== 'all') {
        params.note_type = filterType;
      }
      const res = await getFighterNotes(params);
      setNotes(res.data || []);
    } catch (error) {
      console.error('Failed to load notes:', error);
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    if (user?.role !== 'fighter') {
      navigate('/dashboard');
      return;
    }
    loadNotes();
  }, [user, navigate, loadNotes]);

  const handleCreateNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setCreating(true);
    try {
      await createFighterNote({
        content: newNote.trim(),
        note_type: noteType,
        reference_id: null
      });
      toast.success('Note sent to your coaches!');
      setNewNote('');
      setNoteType('general');
      loadNotes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create note'));
    } finally {
      setCreating(false);
    }
  };

  const getNoteTypeIcon = (type) => {
    switch (type) {
      case 'assessment': return <ClipboardList className="w-4 h-4" />;
      case 'camp': return <Target className="w-4 h-4" />;
      case 'fight': return <Swords className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getNoteTypeLabel = (type) => {
    switch (type) {
      case 'assessment': return 'Assessment';
      case 'camp': return 'Fight Camp';
      case 'fight': return 'Fight';
      default: return 'General';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/fighter/dashboard">
              <Button variant="ghost" size="sm" className="text-zinc-400">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-white font-heading text-lg uppercase tracking-tight">Notes & Communication</h1>
              <p className="text-zinc-500 text-sm">Leave notes for your coaches</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {/* Create New Note */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6"
          >
            <h2 className="text-white font-heading uppercase mb-4 flex items-center gap-2">
              <Send className="w-5 h-5 text-red-500" />
              Send Note to Coaches
            </h2>
            
            <form onSubmit={handleCreateNote} className="space-y-4">
              <div className="space-y-2">
                <label className="text-zinc-400 text-sm">Note Category</label>
                <Select value={noteType} onValueChange={setNoteType}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 w-full md:w-64" data-testid="note-type-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="general">General Message</SelectItem>
                    <SelectItem value="assessment">About Assessment</SelectItem>
                    <SelectItem value="camp">About Fight Camp</SelectItem>
                    <SelectItem value="fight">About Fight</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-zinc-400 text-sm">Your Note</label>
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Write your message to coaches... (questions, feedback, concerns, requests)"
                  className="bg-zinc-950 border-zinc-800 min-h-[120px] resize-none"
                  data-testid="fighter-note-input"
                />
              </div>
              
              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  disabled={creating || !newNote.trim()}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="send-note-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {creating ? 'Sending...' : 'Send Note'}
                </Button>
              </div>
            </form>
          </motion.div>

          {/* Filter */}
          <div className="flex items-center justify-between">
            <h2 className="text-white font-heading uppercase flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              All Notes
            </h2>
            
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800 w-40" data-testid="filter-type-select">
                <Filter className="w-4 h-4 mr-2 text-zinc-400" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="all">All Notes</SelectItem>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="assessment">Assessment</SelectItem>
                <SelectItem value="camp">Fight Camp</SelectItem>
                <SelectItem value="fight">Fight</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes List */}
          {notes.length > 0 ? (
            <div className="space-y-4">
              {notes.map((note, index) => (
                <motion.div
                  key={note.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={cn(
                    "bg-zinc-900/50 border rounded-sm p-5",
                    note.author_role === 'fighter' 
                      ? "border-red-600/30 ml-8" 
                      : "border-zinc-800 mr-8"
                  )}
                  data-testid={`note-item-${note.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                      note.author_role === 'fighter' 
                        ? "bg-red-600/20" 
                        : "bg-blue-600/20"
                    )}>
                      <User className={cn(
                        "w-5 h-5",
                        note.author_role === 'fighter' ? "text-red-500" : "text-blue-500"
                      )} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        <span className="text-white font-medium">{note.author_name}</span>
                        <span className={cn(
                          "text-xs px-2 py-0.5 rounded-sm flex items-center gap-1",
                          note.author_role === 'fighter' 
                            ? "bg-red-600/20 text-red-400" 
                            : "bg-blue-600/20 text-blue-400"
                        )}>
                          {note.author_role === 'fighter' ? 'You' : note.author_role}
                        </span>
                        <span className={cn(
                          "text-xs px-2 py-0.5 rounded-sm flex items-center gap-1 bg-zinc-800 text-zinc-400"
                        )}>
                          {getNoteTypeIcon(note.note_type)}
                          {getNoteTypeLabel(note.note_type)}
                        </span>
                        <span className="text-zinc-500 text-xs">
                          {safeDateFormat(note.created_at, 'M/d/yyyy • h:mm a')}
                        </span>
                      </div>
                      
                      <p className="text-zinc-300 whitespace-pre-wrap">{note.content}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-zinc-900/30 border border-zinc-800 rounded-sm">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-white text-lg mb-2">No Notes Yet</h3>
              <p className="text-zinc-400">Start a conversation with your coaches above</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FighterNotesPage;
